import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import MainLayout from './components/layouts/MainLayout';
import SSOCallback from './pages/auth/SSOCallback';
import Dashboard from './pages/Dashboard';
import FileBrowser from './pages/FileBrowser';
import Profile from './pages/Profile';
import FilePreview from './pages/FilePreview';
import NotFound from './pages/NotFound';
import Filters from './pages/Filters';
import StorageAnalytics from './pages/analytics/StorageAnalytics';
import UsageAnalytics from './pages/analytics/UsageAnalytics';
import UserStorageQuotas from './pages/admin/UserStorageQuotas';
import ActivityDashboard from './pages/ActivityDashboard';
import SearchResults from './pages/SearchResults';
import PrivateRoute from './components/auth/PrivateRoute';
import { RootState } from './store';
import { loadUser } from './store/slices/authSlice';
import websocketService from './services/websocketService';
import NotificationSystem from './components/ui/NotificationSystem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SharedItemViewer from './pages/SharedItemViewer';
import { isStorageAdmin } from './utils/roleUtils';
import { beginDriveReauth, getDriveApiToken } from './lib/auth-token';

const isEditableKeyboardTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('[contenteditable="true"]'));
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, token, user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const loadUserAttempted = React.useRef(false);

  // Load profile when Drive API token is present. Missing/expired session is owned by AuthBootstrap / axios → beginDriveReauth.
  useEffect(() => {
    const loadProfileIfNeeded = async () => {
      if (location.pathname === '/auth/sso/callback') {
        return;
      }
      if (location.pathname.startsWith('/share/')) {
        return;
      }
      // Let AuthBootstrap finish exchanging ?code= / ?sso_token=
      if (
        new URLSearchParams(location.search).has('code') ||
        new URLSearchParams(location.search).has('sso_token')
      ) {
        return;
      }

      if (isAuthenticated && token) {
        return;
      }

      const hasToken = Boolean(getDriveApiToken());
      if (!hasToken || loadUserAttempted.current) {
        return;
      }

      loadUserAttempted.current = true;
      const result = await dispatch(loadUser() as any);
      if (result?.meta?.requestStatus === 'fulfilled') {
        return;
      }

      const payload = result?.payload as
        | { message?: string; status?: number }
        | string
        | undefined;
      const status = typeof payload === 'object' && payload ? payload.status : undefined;
      const message =
        typeof payload === 'string' ? payload : payload?.message;

      // Only reauth on 401 / missing token — never on 403
      if (status === 401 || message === 'No token found') {
        beginDriveReauth({ reason: 'API_401' });
      } else {
        loadUserAttempted.current = false;
      }
    };

    void loadProfileIfNeeded();
  }, [dispatch, location.pathname, location.search, isAuthenticated, token]);

  useEffect(() => {
    if (isAuthenticated && token) {
      websocketService.ensureConnected();
      const shareReturn = localStorage.getItem('share_return_url');
      if (shareReturn) {
        localStorage.removeItem('share_return_url');
        navigate(shareReturn, { replace: true });
      }
    }
  }, [isAuthenticated, token, navigate]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'h') { navigate('/'); e.preventDefault(); }
      if (e.altKey && e.key.toLowerCase() === 'f') { navigate('/files?view=favorites'); e.preventDefault(); }
      if (e.altKey && e.key.toLowerCase() === 'r') { navigate('/files?view=recent'); e.preventDefault(); }
      if (e.altKey && e.key.toLowerCase() === 't') { navigate('/files?view=trash'); e.preventDefault(); }

      if (isEditableKeyboardTarget(e.target)) {
        return;
      }

      if (e.key === 'F5') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'refresh'}})); e.preventDefault(); }
      if (e.ctrlKey && e.key.toLowerCase() === 'f') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'search'}})); e.preventDefault(); }
      if (e.altKey && e.key.toLowerCase() === 'v') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'toggle-view'}})); e.preventDefault(); }
      if (e.key === 'F1') { e.preventDefault(); }
      if (e.ctrlKey && e.key.toLowerCase() === 'a') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'select-all'}})); e.preventDefault(); }
      if (e.ctrlKey && e.key.toLowerCase() === 'c') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'copy'}})); e.preventDefault(); }
      if (e.ctrlKey && e.key.toLowerCase() === 'x') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'cut'}})); e.preventDefault(); }
      if (e.ctrlKey && e.key.toLowerCase() === 'v') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'paste'}})); e.preventDefault(); }
      if (e.key === 'Delete') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'delete'}})); e.preventDefault(); }
      if (e.key === 'F2') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'rename'}})); e.preventDefault(); }
      if (e.shiftKey && e.key.toLowerCase() === 'n') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'new-folder'}})); e.preventDefault(); }
      if (e.shiftKey && e.key.toLowerCase() === 'u') { window.dispatchEvent(new CustomEvent('shortcut',{detail:{action:'upload'}})); e.preventDefault(); }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [navigate]);

  React.useEffect(() => {
    const { enqueueSnackbar } = require('notistack');
    window.enqueueSnackbar = enqueueSnackbar;
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SnackbarProvider 
        maxSnack={3} 
        autoHideDuration={4000}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: 'right' 
        }}
      >
       <Box
  sx={{
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100vh',
    overflow: location.pathname.startsWith('/share/') ? 'auto' : 'hidden',
  }}
>
          <CssBaseline />
          <NotificationSystem />
          
          <Routes>
            <Route path="/auth/sso/callback" element={<SSOCallback />} />
            
            <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
              <Route element={<MainLayout />}>
                <Route path="/" element={<Navigate to="/files" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                
                <Route path="/files" element={<FileBrowser />} />
                <Route path="/files/:folderId" element={<FileBrowser />} />
                <Route path="/files/view/recent" element={<Navigate to="/files?view=recent" replace />} />
                <Route path="/files/view/favorites" element={<Navigate to="/files?view=favorites" replace />} />
                <Route path="/files/view/shared-with-me" element={<Navigate to="/files?view=shared-with-me" replace />} />
                <Route path="/files/view/my-shared" element={<Navigate to="/files?view=my-shared" replace />} />
                <Route path="/files/view/trash" element={<Navigate to="/files?view=trash" replace />} />
                <Route path="/files/view/images" element={<Navigate to="/files?view=images" replace />} />
                <Route path="/files/view/videos" element={<Navigate to="/files?view=videos" replace />} />
                <Route path="/files/view/documents" element={<Navigate to="/files?view=documents" replace />} />
                <Route path="/files/view/audio" element={<Navigate to="/files?view=audio" replace />} />
                <Route path="/files/view/archives" element={<Navigate to="/files?view=archives" replace />} />
                <Route path="/files/view/code" element={<Navigate to="/files?view=code" replace />} />
                <Route path="/trash" element={<Navigate to="/files?view=trash" replace />} />
                <Route path="/favorites" element={<Navigate to="/files?view=favorites" replace />} />
                <Route path="/shared-with-me" element={<Navigate to="/files?view=shared-with-me" replace />} />
                <Route path="/my-shared" element={<Navigate to="/files?view=my-shared" replace />} />
                <Route path="/recent" element={<Navigate to="/files?view=recent" replace />} />
                <Route path="/preview" element={<Navigate to="/files" replace />} />
                <Route path="/preview/:fileId" element={<FilePreview />} />
                
                <Route path="/search" element={<SearchResults />} />

                <Route path="/activity" element={<ActivityDashboard />} />
                <Route path="/activity/user/:userId" element={<ActivityDashboard />} />
                <Route path="/activity/:resourceType/:resourceId" element={<ActivityDashboard />} />
                
                <Route path="/library/images" element={<Navigate to="/files?view=images" replace />} />
                <Route path="/library/videos" element={<Navigate to="/files?view=videos" replace />} />
                <Route path="/library/documents" element={<Navigate to="/files?view=documents" replace />} />
                <Route path="/library/audio" element={<Navigate to="/files?view=audio" replace />} />
                <Route path="/library/archives" element={<Navigate to="/files?view=archives" replace />} />
                <Route path="/library/code" element={<Navigate to="/files?view=code" replace />} />
                
                <Route path="/filters" element={<Filters />} />
                
                <Route path="/analytics/storage" element={<StorageAnalytics />} />
                <Route path="/analytics/usage" element={<UsageAnalytics />} />
                <Route path="/analytics/activity" element={<Navigate to="/activity" replace />} />
                
                <Route path="/profile" element={<Profile />} />
                
                {user && isStorageAdmin(user.role) && (
                  <Route path="/admin/user-storage" element={<UserStorageQuotas />} />
                )}

              </Route>
            </Route>
            
            <Route path="/share/:token" element={<SharedItemViewer />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
      </SnackbarProvider>
    </LocalizationProvider>
  );
};

export default App;
